import * as React from "react";

import type {
  EvaluationDetail,
  EvaluationDraftData,
  EvaluationState,
} from "@/hooks/queries/evaluation-types";
import { resolveResumeStep, type StepId } from "@/routes/evaluation/resolve-resume-step";

// The single shared evaluation-draft store (Story 6.7, Task 5.3).
//
// Hydrated ONCE from the resume read, it is the single source the step components
// (6.3 diagnosis / 6.4 tasks / 6.5 scoring) read their initial values from and write
// edits to. It carries the server `version` so the next save replays it as
// `X-Client-Version` (Story 6.2 optimistic concurrency, re-anchored to the resumed
// version). Framework-light: a React context + reducer — NO new state dependency
// (the codebase uses TanStack Query for server state + local React state, nothing else).

/**
 * A cross-step focus request (Story 6.8 — the Validation Banner's "localized correction
 * link" seam). Set when a correction link jumps to another step; the TARGET step consumes
 * it on mount/update to select the offending task tab and `focus()` the source cell.
 *
 * TRANSIENT by design: it lives OUTSIDE `draft` so it is NEVER serialized into
 * `draft_data` / the save payload (the save body is allow-listed by `toEvaluationDraftBody`
 * — UI focus state must not survive into the persisted clinical record).
 */
export interface FocusTarget {
  step: StepId;
  /** The task tab to select (scoring step). */
  taskCode?: string;
  /** The 0-based cell index to focus (scoring step). */
  itemIndex?: number;
}

export interface EvaluationDraftState {
  /** Evaluation (draft) UUID. */
  id: string;
  /** Client (patient) UUID — plaintext; the NAME is revealed separately (5.4). */
  clientId: string;
  /** Lifecycle state (draft|submitted|scored|archived). */
  state: EvaluationState;
  /** Optimistic-concurrency version — replayed as `X-Client-Version` on save. */
  version: number;
  /** The step the stepper is currently showing. */
  currentStep: StepId;
  /** The whole mutable draft body (diagnosis/tasks/scores/notes/context/date). */
  draft: EvaluationDraftData;
  /** Transient cross-step focus request (NOT persisted) — see `FocusTarget`. */
  pendingFocus: FocusTarget | null;
  /**
   * The opaque `scoring_job_id` from the submit 202 (Story 9.7). TRANSIENT — lives OUTSIDE
   * `draft` so it is never serialized into `draft_data` (it is a client polling handle, not
   * clinical data). Also persisted to localStorage keyed by evaluation id so a same-device
   * reload can resume polling; a fresh mount starts `null` and the results step falls back to
   * the persisted handle (or the cross-device result-poll) when this is null.
   */
  scoringJobId: string | null;
}

export interface EvaluationDraftStore extends EvaluationDraftState {
  /** Navigate the stepper to a step (also persisted into draft.current_step). */
  setCurrentStep: (step: StepId) => void;
  /**
   * Shallow-merge a patch into the draft body (the step editors' write path).
   *
   * CONTRACT — top-level keys only; nested objects are REPLACED, not deep-merged. A
   * caller updating one field of `diagnosis`, or one task's entry in `scores`, MUST
   * spread the current value first:
   *   `patchDraft({ scores: { ...draft.scores, [code]: next } })`
   *   `patchDraft({ diagnosis: { ...draft.diagnosis, location } })`
   * else the sibling keys are dropped. This shallow contract is deliberate, NOT an
   * oversight: a deep auto-merge could never DELETE a nested key, so deselecting a
   * task (6.4 → drop its `scores` entry) or clearing a diagnosis level (6.3's AR26
   * cascade-clear of `specific`/`subtype`) would silently leave stale data in the
   * persisted draft — a worse failure than a lost spread in a clinical record. The
   * step stories (6.3/6.4/6.5) own the read-merge-write. See deferred-work.md.
   */
  patchDraft: (patch: Partial<EvaluationDraftData>) => void;
  /** Re-anchor the concurrency version after a successful save (6.2 returns it). */
  setVersion: (version: number) => void;
  /** Queue (or clear) a cross-step focus request (Story 6.8 correction links). */
  setPendingFocus: (target: FocusTarget | null) => void;
  /** Store the submit 202's opaque `scoring_job_id` for the polling loop (Story 9.7). */
  setScoringJobId: (scoringJobId: string | null) => void;
}

type Action =
  | { type: "setCurrentStep"; step: StepId }
  | { type: "patchDraft"; patch: Partial<EvaluationDraftData> }
  | { type: "setVersion"; version: number }
  | { type: "setPendingFocus"; target: FocusTarget | null }
  | { type: "setScoringJobId"; scoringJobId: string | null }
  | { type: "rehydrate"; detail: EvaluationDetail };

function reducer(state: EvaluationDraftState, action: Action): EvaluationDraftState {
  switch (action.type) {
    case "setCurrentStep":
      // Keep draft.current_step in lockstep so the next save persists it (and a
      // later resume / the 6.10 list read the same field).
      return {
        ...state,
        currentStep: action.step,
        draft: { ...state.draft, current_step: action.step },
      };
    case "patchDraft":
      // Shallow by contract (see `patchDraft` JSDoc): nested objects (diagnosis,
      // scores) are replaced wholesale so callers retain the ability to delete keys;
      // the step editors read-merge-write nested fields themselves.
      return { ...state, draft: { ...state.draft, ...action.patch } };
    case "setVersion":
      return { ...state, version: action.version };
    case "setPendingFocus":
      return { ...state, pendingFocus: action.target };
    case "setScoringJobId":
      return { ...state, scoringJobId: action.scoringJobId };
    case "rehydrate":
      // Reconcile to a strictly-newer server snapshot (gh-449 — version-gated by the
      // provider effect). Rebuild the persisted fields + version from the fresh detail
      // but PRESERVE the live UI step and any transient focus request / scoring handle, so a
      // background refetch never yanks the clinician off the step they're on nor drops the
      // in-flight scoring job id.
      return {
        ...hydrateDraftState(action.detail, state.currentStep),
        pendingFocus: state.pendingFocus,
        scoringJobId: state.scoringJobId,
      };
    default:
      return state;
  }
}

/** Build the initial store state from a resume read (current step derived/honoured). */
export function hydrateDraftState(
  detail: EvaluationDetail,
  initialStep: StepId = resolveResumeStep(detail.draft_data, detail.state),
): EvaluationDraftState {
  return {
    id: detail.id,
    clientId: detail.client_id,
    state: detail.state,
    version: detail.version,
    currentStep: initialStep,
    draft: detail.draft_data ?? {},
    pendingFocus: null,
    scoringJobId: null,
  };
}

const EvaluationDraftContext = React.createContext<EvaluationDraftStore | null>(null);

interface ProviderProps {
  detail: EvaluationDetail;
  /** Override the resolved initial step (defaults to `resolveResumeStep`). */
  initialStep?: StepId;
  children: React.ReactNode;
}

export function EvaluationDraftProvider({ detail, initialStep, children }: ProviderProps) {
  const [state, dispatch] = React.useReducer(
    reducer,
    undefined,
    () =>
      hydrateDraftState(
        detail,
        initialStep ?? resolveResumeStep(detail.draft_data, detail.state),
      ),
  );

  // The lazy initializer above runs ONCE. On a resume, `useGetEvaluation` can hydrate us
  // from a STALE cached snapshot before its refetch resolves (gh-449): the fresh server
  // `detail` then arrives with a strictly-higher `version`, so reconcile the store to it.
  // VERSION-GATED so we never clobber unsaved local edits (which do not bump the version)
  // nor a redundant equal/older refetch — only a genuinely newer server state wins. This is
  // defense-in-depth behind the save-time invalidation (the primary fix) and future-proofs
  // against a `staleTime` change. `lastSyncedVersion` = the highest version the store reflects.
  const lastSyncedVersion = React.useRef(state.version);
  React.useEffect(() => {
    // A local save re-anchored the store to a newer version (`setVersion`) — record it so the
    // matching server refetch is recognized as already-absorbed and never clobbers post-save edits.
    if (state.version > lastSyncedVersion.current) {
      lastSyncedVersion.current = state.version;
    }
    if (detail.version > lastSyncedVersion.current) {
      lastSyncedVersion.current = detail.version;
      dispatch({ type: "rehydrate", detail });
    }
  }, [detail, state.version]);

  const value = React.useMemo<EvaluationDraftStore>(
    () => ({
      ...state,
      setCurrentStep: (step) => dispatch({ type: "setCurrentStep", step }),
      patchDraft: (patch) => dispatch({ type: "patchDraft", patch }),
      setVersion: (version) => dispatch({ type: "setVersion", version }),
      setPendingFocus: (target) => dispatch({ type: "setPendingFocus", target }),
      setScoringJobId: (scoringJobId) => dispatch({ type: "setScoringJobId", scoringJobId }),
    }),
    [state],
  );

  return (
    <EvaluationDraftContext.Provider value={value}>{children}</EvaluationDraftContext.Provider>
  );
}

/** Read the hydrated evaluation-draft store. Throws if used outside the provider. */
export function useEvaluationDraft(): EvaluationDraftStore {
  const ctx = React.useContext(EvaluationDraftContext);
  if (ctx === null) {
    throw new Error("useEvaluationDraft must be used inside <EvaluationDraftProvider>");
  }
  return ctx;
}
